import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Inbox, Phone, Clock, CheckCircle2, UserCheck, ArrowRight, RefreshCw, Gauge } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { API } from "../App";

const SOURCE_LABELS = {
  mhm_lite: "MhM Lite",
  facebook: "Facebook",
  instagram: "Instagram",
  google_ads: "Google Ads",
  channel_partner: "Channel Partner",
  website_form: "Website",
  property_portal: "Portal",
  "99acres": "99acres",
  magicbricks: "MagicBricks",
  housing: "Housing",
};

const SOURCE_COLORS = {
  mhm_lite: "bg-amber-100 text-amber-800 border-amber-300",
  facebook: "bg-blue-100 text-blue-800 border-blue-300",
  instagram: "bg-pink-100 text-pink-800 border-pink-300",
  google_ads: "bg-emerald-100 text-emerald-800 border-emerald-300",
  channel_partner: "bg-violet-100 text-violet-800 border-violet-300",
  website_form: "bg-slate-100 text-slate-800 border-slate-300",
};

const SLA_ROW_BORDER = {
  fresh: "border-l-4 border-l-emerald-500",
  warning: "border-l-4 border-l-amber-500",
  breached: "border-l-4 border-l-red-500",
};

const SLA_DOT = {
  fresh: "bg-emerald-500",
  warning: "bg-amber-500",
  breached: "bg-red-500",
};

function formatMinutes(m) {
  if (m == null) return "—";
  if (m < 1) return "<1m";
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export const ExternalLeadInboxCard = ({ compact = true, maxRows = 5 }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, leads: [], by_source: {} });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchInbox = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lead-inbox?limit=${maxRows}`);
      setData(res.data || { total: 0, leads: [] });
    } catch (e) {
      console.error("inbox fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [maxRows]);

  useEffect(() => {
    fetchInbox();
    const poll = setInterval(fetchInbox, 60000);
    return () => clearInterval(poll);
  }, [fetchInbox]);

  const handleAck = async (leadId) => {
    setBusyId(leadId);
    try {
      await axios.post(`${API}/lead-inbox/${leadId}/acknowledge`, {
        note: "Acknowledged from inbox widget",
      });
      await fetchInbox();
    } finally {
      setBusyId(null);
    }
  };

  const handleClaim = async (leadId) => {
    setBusyId(leadId);
    try {
      await axios.post(`${API}/lead-inbox/${leadId}/claim`);
      await fetchInbox();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200" data-testid="lead-inbox-card-loading">
        <CardContent className="p-6 text-sm text-slate-500">Loading external lead inbox…</CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="bg-white border-slate-200 shadow-sm"
      data-testid="lead-inbox-card"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <Inbox className="w-4 h-4 text-indigo-600" />
            </div>
            External Lead Inbox
            {data.total > 0 && (
              <Badge className="bg-indigo-600 text-white" data-testid="lead-inbox-count">
                {data.total}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchInbox}
              className="p-1.5 rounded-md hover:bg-slate-100 transition"
              title="Refresh"
              data-testid="lead-inbox-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
            </button>
            {compact && data.total > maxRows && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-indigo-600 hover:text-indigo-700"
                onClick={() => navigate("/lead-inbox")}
                data-testid="lead-inbox-view-all"
              >
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
        {Object.keys(data.by_source).length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {Object.entries(data.by_source).map(([src, count]) => (
              <span
                key={src}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${SOURCE_COLORS[src] || "bg-slate-100 text-slate-700 border-slate-200"}`}
              >
                {SOURCE_LABELS[src] || src}: {count}
              </span>
            ))}
            {data.sla_counts && (
              <span className="flex items-center gap-2 text-[11px] text-slate-500 ml-auto" data-testid="lead-inbox-sla-summary">
                {data.sla_counts.fresh > 0 && (
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{data.sla_counts.fresh}</span>
                )}
                {data.sla_counts.warning > 0 && (
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{data.sla_counts.warning}</span>
                )}
                {data.sla_counts.breached > 0 && (
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />{data.sla_counts.breached}</span>
                )}
                {data.avg_response_minutes != null && (
                  <span className="flex items-center gap-1 pl-2 border-l border-slate-200" title={`Based on ${data.avg_response_sample_size} acknowledged lead${data.avg_response_sample_size === 1 ? "" : "s"} in last 24h`} data-testid="lead-inbox-avg-response">
                    <Gauge className="w-3 h-3 text-indigo-500" />
                    Avg response: <strong className="text-slate-700">{formatMinutes(data.avg_response_minutes)}</strong>
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0 space-y-2">
        {data.leads.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400" data-testid="lead-inbox-empty">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            No external leads pending attention.
          </div>
        ) : (
          data.leads.slice(0, maxRows).map((lead) => (
            <div
              key={lead.lead_id}
              className={`flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-200 transition group ${SLA_ROW_BORDER[lead.sla_level] || ""}`}
              data-testid={`lead-inbox-row-${lead.lead_id}`}
              data-sla={lead.sla_level}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => navigate(`/leads/${lead.lead_id}`)}
                    className="font-semibold text-sm text-slate-900 hover:text-indigo-600 truncate"
                    data-testid={`lead-inbox-name-${lead.lead_id}`}
                  >
                    {lead.name}
                  </button>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${SOURCE_COLORS[lead.source] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                  >
                    {SOURCE_LABELS[lead.source] || lead.source}
                  </span>
                  {lead.pending_task && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {lead.pending_task.task_type === "booking_slot" ? "Slot booked" : "Callback"}
                      {lead.pending_task.due_date && ` · ${new Date(lead.pending_task.due_date).toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}`}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>
                  <span className="flex items-center gap-1" data-testid={`lead-inbox-waiting-${lead.lead_id}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${SLA_DOT[lead.sla_level] || "bg-slate-300"}`} />
                    <Clock className="w-3 h-3" />
                    waiting {formatMinutes(lead.minutes_waiting)}
                  </span>
                  <span className="truncate">→ {lead.assigned_to_name}</span>
                </div>
              </div>

              <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition ml-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-indigo-700 hover:bg-indigo-50"
                  disabled={busyId === lead.lead_id}
                  onClick={() => handleClaim(lead.lead_id)}
                  data-testid={`lead-inbox-claim-${lead.lead_id}`}
                >
                  <UserCheck className="w-3.5 h-3.5 mr-1" /> Claim
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                  disabled={busyId === lead.lead_id}
                  onClick={() => handleAck(lead.lead_id)}
                  data-testid={`lead-inbox-ack-${lead.lead_id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default ExternalLeadInboxCard;
